import scheduleRows from "../data/productionDailySchedule.json";
import { mockHolidays } from "./mockData.js";

const API_BASE = import.meta.env.VITE_API_BASE || "https://u5aw5omfkd.execute-api.us-east-2.amazonaws.com/dev";
const USE_MOCK = import.meta.env.VITE_USE_MOCK === "true";

function buildUrl(path, params = {}) {
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      url.searchParams.set(key, value);
    }
  });
  return url;
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function pickLatestRun(items) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const sorted = [...items]
    .filter(item => item && item.run_date)
    .sort((a, b) => String(a.run_date).localeCompare(String(b.run_date)));
  return sorted[sorted.length - 1] || null;
}

function isEmptyRun(data) {
  if (!data || typeof data !== "object") return true;
  return Object.keys(data).length === 0;
}

function findLatestMockRun(projectId, unitNumber) {
  if (!projectId || !unitNumber || !window?.localStorage) return null;
  const prefix = `dailyChecklist:${projectId}:${unitNumber}:`;
  let latestKey = "";
  for (let i = 0; i < localStorage.length; i += 1) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefix)) {
      if (key > latestKey) latestKey = key;
    }
  }
  if (!latestKey) return null;
  const stored = localStorage.getItem(latestKey);
  return stored ? JSON.parse(stored) : null;
}

export async function fetchChecklistCatalog({ catalog_id }) {
  if (USE_MOCK) {
    return { items: scheduleRows.map(row => ({ ...row, catalog_id: catalog_id || "default", operation_key: "GENERAL" })) };
  }
  const res = await fetch(buildUrl("/daily-checklist", { catalog_id }));
  if (!res.ok) throw new Error(`Catalog API error: ${res.status}`);
  return await res.json();
}

export async function fetchHolidaysByProject({ project_id, region }) {
  if (USE_MOCK) {
    const items = (mockHolidays || []).filter(item => !project_id || item.project_id === project_id);
    return { items };
  }
  const res = await fetch(buildUrl("/holidays", { project_id, region }));
  if (!res.ok) throw new Error(`Holidays API error: ${res.status}`);
  return await res.json();
}

export async function fetchChecklistRun({ project_id, unit_number, run_date }) {
  if (USE_MOCK) {
    const key = `dailyChecklist:${project_id}:${unit_number}:${run_date || todayIso()}`;
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
    if (run_date) {
      const latest = findLatestMockRun(project_id, unit_number);
      return latest ? { ...latest, _loaded_from_latest: true } : {};
    }
    return {};
  }
  const res = await fetch(buildUrl("/daily-checklist", { project_id, unit_number, run_date }));
  if (!res.ok) throw new Error(`Run API error: ${res.status}`);
  const data = await res.json();
  if (!isEmptyRun(data) || !run_date) return data;

  const listRes = await fetch(buildUrl("/daily-checklist", { project_id, unit_number }));
  if (!listRes.ok) throw new Error(`Run API error: ${listRes.status}`);
  const listData = await listRes.json();
  const latest = pickLatestRun(listData.items);
  return latest ? { ...latest, _loaded_from_latest: true } : data;
}

export async function saveChecklistRun(payload) {
  if (USE_MOCK) {
    const key = `dailyChecklist:${payload.project_id}:${payload.unit_number}:${payload.run_date}`;
    localStorage.setItem(key, JSON.stringify(payload));
    return { success: true };
  }
  const res = await fetch(`${API_BASE}/daily-checklist`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Run save error: ${res.status}`);
  return await res.json();
}
